import { useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import type { AccountEntity } from '@actual-app/core/types/models';

import type {
  FinancialOnboardingState,
  ProductFinanceClient,
} from '#components/product-finance/client';

import { AccountsStep } from './AccountsStep';
import { CycleStep } from './CyclesStep';
import { IncomeStep } from './IncomeStep';
import { assertNever } from './onboarding-shared';
import { SettingsStep } from './SettingsStep';

export type OnboardingStep = Exclude<
  FinancialOnboardingState['step'],
  'complete'
>;

export type FinancialOnboardingProps = Readonly<{
  initialState: FinancialOnboardingState;
  accounts: readonly AccountEntity[];
  client: ProductFinanceClient;
  mode?: 'onboarding' | 'edit';
  editStep?: 'settings' | 'accounts';
  onComplete: () => Promise<void>;
}>;

export const STEPS: readonly Readonly<{
  id: OnboardingStep;
  label: ReactNode;
}>[] = [
  { id: 'settings', label: <Trans>Preferences</Trans> },
  { id: 'accounts', label: <Trans>Liquid accounts</Trans> },
  { id: 'incomes', label: <Trans>Fixed incomes</Trans> },
  { id: 'cycles', label: <Trans>Budget cycles</Trans> },
];

export function FinancialOnboarding({
  initialState,
  accounts,
  client,
  mode = 'onboarding',
  editStep,
  onComplete,
}: FinancialOnboardingProps) {
  const [onboarding, setOnboarding] =
    useState<FinancialOnboardingState>(initialState);
  const [activeStep, setActiveStep] = useState<OnboardingStep>(
    mode === 'edit'
      ? (editStep ?? 'settings')
      : getIncompleteStep(initialState),
  );
  const activeAccounts = accounts.filter(account => !account.closed);

  async function refresh(nextStep?: OnboardingStep) {
    const next = await client.loadOnboarding();
    setOnboarding(next);
    setActiveStep(nextStep ?? getIncompleteStep(next));
    return next;
  }

  return (
    <View
      style={{
        width: '100%',
        maxWidth: 720,
        alignSelf: 'center',
        gap: 14,
        flexShrink: 0,
      }}
    >
      <View>
        <h1 style={{ margin: 0, fontSize: 25 }}>
          {mode === 'edit' ? (
            <Trans>Financial settings</Trans>
          ) : (
            <Trans>Set up your financial plan</Trans>
          )}
        </h1>
        <p style={{ marginBottom: 0, color: theme.pageTextSubdued }}>
          {mode === 'edit' ? (
            <Trans>
              Update the preferences and accounts used by your financial
              dashboard.
            </Trans>
          ) : (
            <Trans>
              Complete each step to calculate your available money without
              making assumptions about your finances.
            </Trans>
          )}
        </p>
      </View>

      {editStep === undefined && (
        <StepNavigation
          activeStep={activeStep}
          onboarding={onboarding}
          mode={mode}
          onSelect={setActiveStep}
        />
      )}

      {activeStep === 'settings' && (
        <SettingsStep
          onboarding={onboarding}
          client={client}
          onSaved={async () => {
            if (mode === 'edit' && editStep === 'settings') {
              await onComplete();
            } else {
              await refresh('accounts');
            }
          }}
        />
      )}
      {activeStep === 'accounts' && (
        <AccountsStep
          onboarding={onboarding}
          accounts={activeAccounts}
          client={client}
          finishLabel={
            mode === 'edit' ? <Trans>Save accounts</Trans> : undefined
          }
          onSaved={async () => {
            if (mode === 'edit') {
              await onComplete();
            } else {
              await refresh('incomes');
            }
          }}
        />
      )}
      {activeStep === 'incomes' && mode === 'onboarding' && (
        <IncomeStep
          onboarding={onboarding}
          client={client}
          onCreated={() => refresh('incomes')}
          onContinue={() => setActiveStep('cycles')}
        />
      )}
      {activeStep === 'cycles' && mode === 'onboarding' && (
        <CycleStep
          onboarding={onboarding}
          client={client}
          onCreated={() => refresh('cycles')}
          onComplete={onComplete}
        />
      )}
    </View>
  );
}

export function StepNavigation({
  activeStep,
  onboarding,
  mode,
  onSelect,
}: Readonly<{
  activeStep: OnboardingStep;
  onboarding: FinancialOnboardingState;
  mode: 'onboarding' | 'edit';
  onSelect: (step: OnboardingStep) => void;
}>) {
  const { t } = useTranslation();
  const visibleSteps = mode === 'edit' ? STEPS.slice(0, 2) : STEPS;
  return (
    <ol
      aria-label={t('Financial setup progress')}
      style={{
        display: 'grid',
        // Wrap into two columns on narrow screens instead of clipping labels.
        gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
        listStyle: 'none',
        padding: 0,
        margin: 0,
        gap: 6,
      }}
    >
      {visibleSteps.map((step, index) => {
        const isAccessible = canOpenStep(step.id, onboarding);
        return (
          <li key={step.id}>
            <button
              type="button"
              aria-current={activeStep === step.id ? 'step' : undefined}
              disabled={!isAccessible}
              onClick={() => onSelect(step.id)}
              style={{
                width: '100%',
                minHeight: 48,
                padding: '7px 6px',
                border: `1px solid ${
                  activeStep === step.id
                    ? theme.buttonPrimaryBackground
                    : theme.cardBorder
                }`,
                borderRadius: 6,
                background:
                  activeStep === step.id
                    ? theme.noticeBackground
                    : theme.cardBackground,
                color: isAccessible ? theme.pageText : theme.pageTextSubdued,
                cursor: isAccessible ? 'pointer' : 'default',
              }}
            >
              <span style={{ display: 'block', fontSize: 12 }}>
                <Trans>Step {{ stepNumber: index + 1 }}</Trans>
              </span>
              <strong>{step.label}</strong>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

export function getIncompleteStep(
  state: FinancialOnboardingState,
): OnboardingStep {
  return state.step === 'complete' ? 'cycles' : state.step;
}

export function canOpenStep(
  step: OnboardingStep,
  state: FinancialOnboardingState,
): boolean {
  switch (step) {
    case 'settings':
      return true;
    case 'accounts':
      return state.settings !== null;
    case 'incomes':
      return state.settings !== null && state.liquidAccountIds.length > 0;
    case 'cycles':
      return state.fixedIncomeTemplates.length > 0;
    default:
      return assertNever(step);
  }
}
